import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import type { SocialNetwork } from '@agency-smm/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MegaPromptSections {
  systemPrompt: string;
  generationRules: string;
  dynamicTextRules: string;
  imageRulesStock: string;
  imageRulesAi: string;
}

interface GeneratedPost {
  index: number;
  content: string;
  stockImagePrompt: string | null;
  aiImagePrompt: string | null;
  headline: string;
  tagline: string;
  cta: string;
  quote: string;
}

interface GenerateResponse {
  posts: GeneratedPost[];
  failedIndices: number[];
  assembledPrompt: string;
  systemPrompt: string;
  provider: string;
}

interface PreviewResponse {
  systemPrompt: string;
  prompt: string;
  assignments: Array<{
    index: number;
    network: string;
    category: string;
    formatName: string;
    imageType: string;
  }>;
  sections: MegaPromptSections;
}

const NETWORKS: { value: SocialNetwork; label: string }[] = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'threads', label: 'Threads' },
  { value: 'gbp', label: 'GBP' },
  { value: 'bluesky', label: 'Bluesky' },
];

const SECTION_LABELS: Record<keyof MegaPromptSections, string> = {
  systemPrompt: 'System Prompt',
  generationRules: 'Generation Rules',
  dynamicTextRules: 'Dynamic Text Rules',
  imageRulesStock: 'Stock Image Rules',
  imageRulesAi: 'AI Image Rules',
};

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    height: auto;
    overflow: visible;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const MainLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  flex: 1;
  min-height: 0;

  @media (max-width: 900px) {
    flex-direction: column;
    min-height: auto;
  }
`;

// Left panel: config + actions + results (scrollable)
const LeftPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing.sm};

  @media (max-width: 900px) {
    overflow: visible;
    padding-right: 0;
  }
`;

// Right panel: editable sections (scrollable, fixed width)
const RightPanel = styled.div`
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-left: ${({ theme }) => theme.borders.thin};
  padding-left: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 900px) {
    width: 100%;
    border-left: none;
    border-top: ${({ theme }) => theme.borders.thin};
    padding-left: 0;
    padding-top: ${({ theme }) => theme.spacing.lg};
  }
`;

const PanelTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ConfigBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: wrap;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const ConfigLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
`;

const Select = styled.select`
  padding: 6px 10px;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const CountInput = styled.input`
  width: 60px;
  padding: 6px 10px;
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
`;

const NetworkCheckboxes = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  flex-wrap: wrap;
`;

const NetworkChip = styled.label<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $active }) => $active ? theme.colors.primary + '15' : 'transparent'};
  color: ${({ theme, $active }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;

  input { display: none; }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SectionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  min-height: 0;
`;

const SectionCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const SectionBadge = styled.span<{ $modified?: boolean }>`
  font-size: 10px;
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme, $modified }) => $modified ? theme.colors.warning + '30' : theme.colors.surfaceHover};
  color: ${({ theme, $modified }) => $modified ? theme.colors.warning : theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const SectionBody = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => $open ? 'block' : 'none'};
  padding: ${({ theme }) => theme.spacing.sm};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: ${({ theme }) => theme.spacing.sm};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 12px;
  font-family: 'Courier New', Consolas, monospace;
  line-height: 1.4;
  resize: vertical;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const ResultsSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ResultsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ResultsTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const ProviderBadge = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 4px 10px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.secondary}20;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const PostCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PostMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`;

const MetaTag = styled.span<{ $color?: string }>`
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $color }) => ($color || '#3B82F6') + '18'};
  color: ${({ $color }) => $color || '#3B82F6'};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const PostContent = styled.pre`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const DynamicTextsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DtItem = styled.div`
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: ${({ theme }) => theme.borders.thin};
`;

const DtLabel = styled.div`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const DtValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const PromptPreview = styled.pre`
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: 12px;
  font-family: 'Courier New', Consolas, monospace;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusBar = styled.div<{ $type?: 'info' | 'error' | 'success' }>`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;
  background: ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error + '15'
    : $type === 'success' ? theme.colors.success + '15'
    : theme.colors.primary + '10'};
  color: ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error
    : $type === 'success' ? theme.colors.success
    : theme.colors.primary};
  border: 1px solid ${({ theme, $type }) =>
    $type === 'error' ? theme.colors.error + '30'
    : $type === 'success' ? theme.colors.success + '30'
    : theme.colors.primary + '20'};
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

const ResetLink = styled.button`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const SaveBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  margin-top: auto;
  flex-shrink: 0;
  border-top: ${({ theme }) => theme.borders.thin};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ModPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workspaces } = useWorkspaces();

  // Config state
  const [workspaceId, setWorkspaceId] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<SocialNetwork[]>(['instagram']);
  const [count, setCount] = useState(3);

  // Sections state
  const [sections, setSections] = useState<MegaPromptSections | null>(null);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [editedSections, setEditedSections] = useState<Partial<MegaPromptSections>>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Preview / results
  const [previewPrompt, setPreviewPrompt] = useState<string | null>(null);
  const [results, setResults] = useState<GenerateResponse | null>(null);

  // Loading / status
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);

  // Auth check
  const role = user?.role;
  useEffect(() => {
    if (role !== 'mod' && role !== 'admin') {
      navigate('/dashboard');
    }
  }, [role, navigate]);

  // Load sections on mount
  useEffect(() => {
    loadSections();
  }, []);

  // Auto-select first workspace
  useEffect(() => {
    if (workspaces.length > 0 && !workspaceId) {
      setWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, workspaceId]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ sections: MegaPromptSections; defaults: Record<string, string> }>(
        '/api/mod/playground/sections'
      );
      setSections(response.sections);
      setDefaults(response.defaults);
      setEditedSections({});
    } catch (err) {
      setStatus({ text: `Failed to load: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSectionValue = useCallback((key: keyof MegaPromptSections): string => {
    if (editedSections[key] !== undefined) return editedSections[key]!;
    if (sections) return sections[key];
    return '';
  }, [sections, editedSections]);

  const isSectionModified = useCallback((key: keyof MegaPromptSections): boolean => {
    return editedSections[key] !== undefined && editedSections[key] !== sections?.[key];
  }, [sections, editedSections]);

  const hasAnyEdits = Object.keys(editedSections).some(k =>
    editedSections[k as keyof MegaPromptSections] !== sections?.[k as keyof MegaPromptSections]
  );

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSectionEdit = (key: keyof MegaPromptSections, value: string) => {
    setEditedSections(prev => ({ ...prev, [key]: value }));
  };

  const handleResetSection = (key: keyof MegaPromptSections) => {
    const promptKeyMap: Record<keyof MegaPromptSections, string> = {
      systemPrompt: 'mega_system_prompt',
      generationRules: 'mega_generation_rules',
      dynamicTextRules: 'mega_dynamic_text_rules',
      imageRulesStock: 'mega_image_rules_stock',
      imageRulesAi: 'mega_image_rules_ai',
    };
    const defaultValue = defaults[promptKeyMap[key]];
    if (defaultValue !== undefined) {
      setEditedSections(prev => ({ ...prev, [key]: defaultValue }));
    }
  };

  const toggleNetwork = (network: SocialNetwork) => {
    setSelectedNetworks(prev => {
      if (prev.includes(network)) {
        if (prev.length === 1) return prev;
        return prev.filter(n => n !== network);
      }
      return [...prev, network];
    });
  };

  const handlePreview = async () => {
    if (!workspaceId) {
      setStatus({ text: 'Select a workspace first', type: 'error' });
      return;
    }
    setPreviewing(true);
    setStatus(null);
    try {
      const response = await apiClient.post<PreviewResponse>('/api/mod/playground/preview', {
        workspaceId,
        networks: selectedNetworks,
        count,
        sections: hasAnyEdits ? editedSections : undefined,
      });
      setPreviewPrompt(`=== SYSTEM PROMPT ===\n${response.systemPrompt}\n\n=== USER PROMPT ===\n${response.prompt}`);
    } catch (err) {
      setStatus({ text: `Preview failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (!workspaceId) {
      setStatus({ text: 'Select a workspace first', type: 'error' });
      return;
    }
    setGenerating(true);
    setStatus({ text: 'Generating... this may take 15-60 seconds', type: 'info' });
    setResults(null);
    setPreviewPrompt(null);
    try {
      const response = await apiClient.post<GenerateResponse>('/api/mod/playground/generate', {
        workspaceId,
        networks: selectedNetworks,
        count,
        sections: hasAnyEdits ? editedSections : undefined,
      });
      setResults(response);
      setStatus({
        text: `Generated ${response.posts.length} posts via ${response.provider}${response.failedIndices.length > 0 ? ` (${response.failedIndices.length} failed)` : ''}`,
        type: response.posts.length > 0 ? 'success' : 'error',
      });
    } catch (err) {
      setStatus({ text: `Generation failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!hasAnyEdits) return;
    setSaving(true);
    setStatus(null);
    try {
      const sectionsToSave: Partial<MegaPromptSections> = {};
      for (const [key, val] of Object.entries(editedSections)) {
        if (val !== sections?.[key as keyof MegaPromptSections]) {
          (sectionsToSave as Record<string, string>)[key] = val as string;
        }
      }
      await apiClient.post('/api/mod/playground/save-sections', { sections: sectionsToSave });
      setStatus({ text: 'Sections saved. All future generations will use these prompts.', type: 'success' });
      await loadSections();
    } catch (err) {
      setStatus({ text: `Save failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (role !== 'mod' && role !== 'admin') return null;

  if (loading) {
    return (
      <PageWrapper>
        <LoadingOverlay>Loading playground...</LoadingOverlay>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <Title>Prompt Playground</Title>
        <Button $variant="outline" $size="sm" onClick={() => navigate('/dashboard')}>
          Back
        </Button>
      </Header>

      <MainLayout>
        {/* LEFT: Config + Actions + Results */}
        <LeftPanel>
          <ConfigBar>
            <ConfigLabel>Workspace</ConfigLabel>
            <Select value={workspaceId} onChange={e => setWorkspaceId(e.target.value)}>
              <option value="">Select...</option>
              {workspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </Select>

            <ConfigLabel>Networks</ConfigLabel>
            <NetworkCheckboxes>
              {NETWORKS.map(n => (
                <NetworkChip key={n.value} $active={selectedNetworks.includes(n.value)}>
                  <input
                    type="checkbox"
                    checked={selectedNetworks.includes(n.value)}
                    onChange={() => toggleNetwork(n.value)}
                  />
                  {n.label}
                </NetworkChip>
              ))}
            </NetworkCheckboxes>

            <ConfigLabel>Posts</ConfigLabel>
            <CountInput
              type="number"
              min={1}
              max={5}
              value={count}
              onChange={e => setCount(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
            />
          </ConfigBar>

          <ActionBar>
            <Button
              $variant="outline"
              $size="sm"
              onClick={handlePreview}
              disabled={previewing || !workspaceId}
            >
              {previewing ? 'Building...' : 'Preview Prompt'}
            </Button>
            <Button
              $size="sm"
              onClick={handleGenerate}
              disabled={generating || !workspaceId}
            >
              {generating ? 'Generating...' : 'Generate Test'}
            </Button>
          </ActionBar>

          {status && <StatusBar $type={status.type}>{status.text}</StatusBar>}

          {previewPrompt && (
            <>
              <ResultsTitle style={{ marginBottom: '8px' }}>Assembled Prompt</ResultsTitle>
              <PromptPreview>{previewPrompt}</PromptPreview>
            </>
          )}

          {results && results.posts.length > 0 && (
            <ResultsSection>
              <ResultsHeader>
                <ResultsTitle>Results ({results.posts.length} posts)</ResultsTitle>
                <ProviderBadge>{results.provider}</ProviderBadge>
              </ResultsHeader>

              {results.posts.map(post => (
                <PostCard key={post.index}>
                  <PostMeta>
                    <MetaTag $color="#3B82F6">Post {post.index}</MetaTag>
                    {post.stockImagePrompt && (
                      <MetaTag $color="#10B981">stock: {post.stockImagePrompt}</MetaTag>
                    )}
                    {post.aiImagePrompt && (
                      <MetaTag $color="#8B5CF6">ai image</MetaTag>
                    )}
                  </PostMeta>
                  <PostContent>{post.content}</PostContent>
                  <DynamicTextsGrid>
                    <DtItem>
                      <DtLabel>Headline</DtLabel>
                      <DtValue>{post.headline}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>Tagline</DtLabel>
                      <DtValue>{post.tagline}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>CTA</DtLabel>
                      <DtValue>{post.cta}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>Quote</DtLabel>
                      <DtValue>{post.quote}</DtValue>
                    </DtItem>
                  </DynamicTextsGrid>
                  {post.aiImagePrompt && (
                    <DtItem style={{ marginTop: '8px' }}>
                      <DtLabel>AI Image Prompt</DtLabel>
                      <DtValue style={{ fontSize: '12px', color: '#64748B' }}>{post.aiImagePrompt}</DtValue>
                    </DtItem>
                  )}
                </PostCard>
              ))}

              <details style={{ marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#64748B', marginBottom: '8px' }}>
                  View assembled prompt used
                </summary>
                <PromptPreview>{`=== SYSTEM PROMPT ===\n${results.systemPrompt}\n\n=== USER PROMPT ===\n${results.assembledPrompt}`}</PromptPreview>
              </details>
            </ResultsSection>
          )}
        </LeftPanel>

        {/* RIGHT: Editable Prompt Sections */}
        <RightPanel>
          <PanelTitle>Prompt Sections</PanelTitle>

          <SectionsStack>
            {(Object.keys(SECTION_LABELS) as Array<keyof MegaPromptSections>).map(key => (
              <SectionCard key={key}>
                <SectionHeader onClick={() => toggleSection(key)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                      {expandedSections.has(key) ? '\u25BC' : '\u25B6'}
                    </span>
                    <SectionTitle>{SECTION_LABELS[key]}</SectionTitle>
                    {isSectionModified(key) && <SectionBadge $modified>edited</SectionBadge>}
                  </div>
                  {expandedSections.has(key) && (
                    <ResetLink
                      onClick={e => { e.stopPropagation(); handleResetSection(key); }}
                      title="Reset to default"
                    >
                      reset
                    </ResetLink>
                  )}
                </SectionHeader>
                <SectionBody $open={expandedSections.has(key)}>
                  <Textarea
                    value={getCurrentSectionValue(key)}
                    onChange={e => handleSectionEdit(key, e.target.value)}
                    rows={6}
                  />
                </SectionBody>
              </SectionCard>
            ))}
          </SectionsStack>

          {hasAnyEdits && (
            <SaveBar>
              <Button
                $variant="secondary"
                $size="sm"
                $fullWidth
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save as Active'}
              </Button>
            </SaveBar>
          )}
        </RightPanel>
      </MainLayout>
    </PageWrapper>
  );
}
